import React, { useContext } from 'react';
import {
	OfferContext,
	IPriceComponent,
	getPriceAmountByCatalogId,
	getVignetteByCatalogAndFuel,
	VignetteContext,
	ITypeVignetteOrderingProcessFields,
	getLabelValue,
	IDowntime,
} from '@adac-mp/shared';
import { HintMessage, IconPopup, TollRoute, Typography, VignetteAUT, VignetteCH, getDrawerTitle } from '../../index';

interface IProductOptionLabelProps {
	catalogId: string;
	serviceCatalogName: string;
	supplierId?: number;
	priceComponents: IPriceComponent[];
	moduleTypeId: number;
	downtimes: IDowntime[];
	teaserFuelId?: number;
	selectedFuelId?: number;
}
export const ProductOptionLabel = ({
	catalogId,
	serviceCatalogName,
	priceComponents,
	moduleTypeId,
	downtimes,
	teaserFuelId = undefined,
	selectedFuelId = -1,
	supplierId = undefined,
}: IProductOptionLabelProps) => {
	const { vignetteOffers, labels } = useContext(OfferContext);
	const { isTransitionPeriod, contentfulLabels } = useContext(VignetteContext);
	const currentLabels = contentfulLabels as ITypeVignetteOrderingProcessFields;
	const generalVignette = getVignetteByCatalogAndFuel(vignetteOffers, catalogId, -1);
	const generalPriceComponents = generalVignette?.priceComponents ?? [];
	const generalPrice = getPriceAmountByCatalogId(generalPriceComponents);
	const price = getPriceAmountByCatalogId(priceComponents);
	const hasServiceFee = priceComponents.find((priceComponent) => priceComponent.serviceItemId === 8);
	const flexTollSection = moduleTypeId === 4;

	const getTeaserPrice = () => {
		if (!teaserFuelId || vignetteOffers.length === 0) {
			return undefined;
		}
		return getPriceAmountByCatalogId(priceComponents);
	};
	const teaserPrice = getTeaserPrice();

	const getPriceLabel = () => {
		const showFromPrice =
			supplierId !== 14 &&
			(moduleTypeId === 3 ||
				(isTransitionPeriod &&
					vignetteOffers.filter((vignette) => vignette.serviceCatalogName === serviceCatalogName).length >
						2));

		return `${showFromPrice ? getLabelValue('mp.from', labels) || 'ab' : ''} ${teaserPrice ?? price}€`;
	};

	const renderProductIcon = () => {
		if (supplierId === 14) {
			return <VignetteCH iconAsBadge />;
		}
		if (supplierId !== 3) return;
		if (moduleTypeId === 4) {
			return (
				<IconPopup
					bold
					paddingX="large"
					title={getDrawerTitle('tollSectionFlex', currentLabels?.helpDrawers)}
					type="info"
					contentfulId="tollSectionFlex"
				/>
			);
		}
		return moduleTypeId === 2 ? <VignetteAUT iconAsBadge /> : <TollRoute iconAsBadge />;
	};

	return (
		<>
			<span className="flex justify-between gap-2 items-center md:items-start">
				<span>
					<Typography variant="title" size="lg" component="p" color="title">
						{serviceCatalogName}
					</Typography>
					{hasServiceFee && (
						<Typography color="lightText">
							{getLabelValue('serviceFee', labels) || 'Zzgl. Servicegebühr'}
						</Typography>
					)}
					{flexTollSection && (
						<Typography color="lightText">
							{currentLabels?.cart?.allTollRoutesUsable || 'Alle Streckenmautabschnitte nutzbar'}
						</Typography>
					)}
				</span>
				<span className="flex gap-2">
					{!flexTollSection && (
						<span className="flex flex-col">
							<Typography
								className="whitespace-nowrap"
								color="title"
								variant="title"
								size="lg"
								component="p">
								{getPriceLabel()}
							</Typography>
							{(selectedFuelId !== -1 || teaserPrice) && (
								<Typography color="lightText" className="line-through">{`${generalPrice}€`}</Typography>
							)}
						</span>
					)}
					{renderProductIcon()}
				</span>
			</span>
			{flexTollSection &&
				downtimes.map(({ id, message }) => (
					<HintMessage
						key={id}
						className="-ml-5 mt-3"
						padding="small"
						hints={[{ tip: 'Hinweis:', message }]}
					/>
				))}
		</>
	);
};
